import React from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { cn, iconSize } from '@fabric-ui/utils';
import { Collapse, Fade, IconButton, Stack, styled } from '@mui/material';
import { InlineAction } from './KeyValueExplorer.utils';

type KeyValueExplorerRowProps<TItem> = {
  keyName: string;
  value: string;
  item: TItem;
  inlineActions?: InlineAction<TItem>[];
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpanded?: (key: string) => void;
  ExpandedComponent?: ExplorerRenderFunction<TItem>;
};

export type ExplorerRenderFunctionProps<TItem> = { item: TItem };
export type ExplorerRenderFunction<TItem> = React.ComponentType<ExplorerRenderFunctionProps<TItem>>;

export const explorerClassNames = {
  row: 'KeyValueExplorer__Row',
  key: 'KeyValueExplorer__RowKey',
  value: 'KeyValueExplorer__RowValue',
  actions: 'KeyValueExplorer__RowActions',
  tag: 'KeyValueExplorer__RowTag',
} as const;
export const EXPLORER_ROW_MIN_HEIGHT = 32;

const StyledRow = styled('div', { name: 'KeyValueExplorerRow' })(({ theme: { transitions } }) => ({
  position: 'relative',
  padding: '5px 8px 5px 0px',
  borderRadius: '8px',
  border: '1px solid transparent',
  transition: transitions.create('background-color'),
  ':hover': { backgroundColor: 'var(--color-surface-table-row-hover)' },
  [`:hover .${explorerClassNames.actions}`]: { opacity: 1 },
  '& dt': {
    backgroundColor: 'var(--color-surface-interactive-modifier-default)',
    padding: '2px 6px',
    borderRadius: '8px',
    fontWeight: 500,
    lineHeight: 'normal',
    '&:after': { content: `':'` },
    '+ dd': { marginLeft: '4px', wordBreak: 'break-all' },
  },
  '& dt, & dd': { display: 'inline', lineHeight: 'normal' },
  [`.${explorerClassNames.actions}`]: {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    paddingLeft: '32px',
    paddingRight: '16px',
    gap: '16px',
    opacity: 0,
    borderRadius: '8px',
    backgroundImage: 'linear-gradient(to right, transparent, var(--color-surface-table-row-hover) 32px)',
  },

  '&[data-expanded="true"]': {
    borderRadius: '8px 8px 0 0',
  },

  [`&:not(.expandable):has(.${explorerClassNames.tag}:not(:empty))`]: {
    borderColor: 'var(--color-border-base-primary)',
  },

  '&.expandable': {
    borderColor: 'var(--dataviz-color-sequential-blue-scale02)',
    cursor: 'pointer',
  },
}));

export const KeyValueExplorerRow = <TItem,>({
  ref,
  keyName,
  value,
  item,
  inlineActions,
  className,
  onPointerEnter,
  onPointerLeave,
  expandable: _expandable,
  expanded = false,
  onToggleExpanded,
  ExpandedComponent,
  children,
  ...props
}: KeyValueExplorerRowProps<TItem> & React.ComponentProps<typeof StyledRow>) => {
  const [isHover, setIsHover] = React.useState(false);
  const id = React.useId();
  const expandable = Boolean(_expandable && ExpandedComponent);

  return (
    <div>
      <StyledRow
        className={cn(explorerClassNames.row, { expandable }, className)}
        data-expanded={expanded}
        ref={ref}
        onClick={expandable ? () => onToggleExpanded?.(keyName) : undefined}
        onPointerEnter={e => {
          setIsHover(true);
          onPointerEnter?.(e);
        }}
        onPointerLeave={e => {
          setIsHover(false);
          onPointerLeave?.(e);
        }}
        {...props}>
        {expandable ? (
          <AvTooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${keyName}`}
              size="small"
              onClick={e => {
                e.stopPropagation();
                onToggleExpanded?.(keyName);
              }}
              sx={{
                padding: '2px',
                margin: '0 8px',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}>
              <ArrowDown aria-hidden="true" style={iconSize(16)} />
            </IconButton>
          </AvTooltip>
        ) : null}

        <dt id={id} className={explorerClassNames.key}>
          {keyName}
        </dt>
        <dd aria-labelledby={id} className={explorerClassNames.value}>
          {value}
        </dd>
        {children ? <span className={explorerClassNames.tag}>{children}</span> : null}
        <Fade in={Boolean(isHover && inlineActions?.length)} mountOnEnter unmountOnExit>
          <Stack
            role="group"
            aria-label={`Actions for ${keyName}`}
            direction="row"
            alignItems="center"
            className={explorerClassNames.actions}
            onClick={e => e.stopPropagation()}>
            {inlineActions?.map((action, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={i}>{action(item)}</React.Fragment>
            ))}
          </Stack>
        </Fade>
      </StyledRow>

      {expandable && ExpandedComponent ? (
        <Collapse data-highlight-skip in={Boolean(expandable && expanded)} unmountOnExit mountOnEnter enter={false} exit={false}>
          <ExpandedComponent item={item} />
        </Collapse>
      ) : null}
    </div>
  );
};
